/* eslint-disable import/prefer-default-export */
import { KustomerErrorResponse } from 'globals/types';

import { getTrackingTokenFromLocalStorage } from 'helpers/browser';
import { getStore } from 'core_main/store';
import { SCHEDULES_ENDPOINT } from 'core_main/api/schedules/constants';
import { getEnv } from 'core_main/globals/env';
import {
  timeEventStart,
  timeEventStop,
  transformApiEndpointToEventName,
} from 'globals/eventTracking/helpers';
import { AllEndpoints, KustomerOptions, KustomerResponse } from './types';

export const createOptionsWithHeaders = (options) => {
  const trackingToken = getTrackingTokenFromLocalStorage() || '';

  const headers = new Headers({
    'Content-Type': 'application/json',
    'x-kustomer': 'kustomer',
    'x-kustomer-client': 'chat-web',
    'x-kustomer-tracking-token': trackingToken,
    credentials: 'include',
  });

  return {
    ...options,
    headers,
  };
};

export const parseErrors = (errors) => {
  const error = errors[0];
  const subError = error?.meta?.subErrors?.[0];
  const { title: errorTitle, code: errorCode, source: errorSource } = error;

  if (subError) {
    const subErrorTitle = subError.title;
    const subErrorCode = subError.code;
    const subErrorSource = subError.source;

    return {
      code: subErrorCode,
      title: subErrorTitle,
      source: subErrorSource.pointer,
    };
  }

  return {
    source: errorSource?.parameter || errorSource?.pointer,
    title: errorTitle,
    code: errorCode,
  };
};

const handleErrorResponse = (res: Response) => {
  return res.json().then((json) => {
    const parsedError = parseErrors(json.errors);

    let errorMessage = `${res?.status} ${res?.statusText} to ${res?.url}`;

    if (parsedError.code === 'badparam') {
      errorMessage = `${parsedError?.title}: ${parsedError?.source}`;
    }

    const error = new Error(errorMessage) as KustomerErrorResponse;

    error.title = parsedError?.title;
    error.status = res.status ? `${res.status}` : '';
    error.source = parsedError?.source;
    error.url = res?.url;
    error.type = res?.type;

    throw error;
  });
};

export const handleFetchResponse = (res: Response) => {
  if (res.statusText === 'No Content') return undefined;

  const contentType = res.headers.get('content-type');

  if (!contentType || !contentType.includes('application/json')) return null;

  if (res.ok) {
    return res?.json();
  }
  return handleErrorResponse(res);
};

export const uploadToS3 = (url: string, formData: FormData) => {
  return fetch(url, { method: 'POST', body: formData }).then((res) => {
    return handleFetchResponse(res);
  });
};

export const apiFetch = <U extends AllEndpoints, O extends KustomerOptions>(
  endpoint: U,
  options?: O,
): Promise<KustomerResponse<U, O['method']>> => {
  const { protocol, orgName, baseUrlDomain } = getEnv();
  if (orgName === undefined) return Promise.reject(new Error('Missing org'));
  const store = getStore();

  const enabled = store?.settings?.enabled;
  const passableEndpoints = [SCHEDULES_ENDPOINT];

  if (enabled === false && !passableEndpoints.includes(endpoint)) {
    return Promise.reject(
      new Error(
        'Could not complete request because Kustomer Chat is disabled in your org settings',
      ),
    );
  }

  let base = `${protocol}://${orgName}.${baseUrlDomain}${endpoint}`;

  if (
    process.env.NODE_ENV !== 'production' &&
    process.env.DEV_REMOTE_API_BASE_URL
  ) {
    base = `${process.env.DEV_REMOTE_API_BASE_URL}${endpoint}`;
  }

  const optionsWithHeaders = createOptionsWithHeaders(options);

  // start api event timer
  const name = transformApiEndpointToEventName(endpoint, options?.method);
  if (!options?.ignoreTracking) {
    timeEventStart({ name });
  }

  return fetch(base, optionsWithHeaders)
    .then((res) => {
      return handleFetchResponse(res);
    })
    .finally(() => {
      // event timer stop goes here
      if (!options?.ignoreTracking) {
        timeEventStop({ name });
      }
    });
};

export const get = <
  U extends AllEndpoints,
  O extends Omit<KustomerOptions, 'method'>,
>(
  endpoint: U,
  options?: O,
) => {
  const optionsWithMethod = {
    method: 'GET' as const,
    ...options,
  };
  return apiFetch(endpoint, optionsWithMethod);
};

export const post = <
  U extends AllEndpoints,
  O extends Omit<KustomerOptions, 'method'>,
>(
  endpoint: U,
  options?: O,
) => {
  const optionsWithMethod = {
    method: 'POST' as const,
    ...options,
  };
  return apiFetch(endpoint, optionsWithMethod);
};

export const put = <
  U extends AllEndpoints,
  O extends Omit<KustomerOptions, 'method'>,
>(
  endpoint: U,
  options?: O,
) => {
  const optionsWithMethod = {
    method: 'PUT' as const,
    ...options,
  };
  return apiFetch(endpoint, optionsWithMethod);
};

export const patch = <
  U extends AllEndpoints,
  O extends Omit<KustomerOptions, 'method'>,
>(
  endpoint: U,
  options?: O,
) => {
  const optionsWithMethod = {
    method: 'PATCH' as const,
    ...options,
  };
  return apiFetch(endpoint, optionsWithMethod);
};
